import { useEffect } from "react";

const SITE = "QTV Network";
const DEFAULT_IMAGE = "https://qtvstream.com/og-image.jpg";

const setMeta = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

// Per-page title, description, social preview tags, canonical URL and optional JSON-LD.
export const usePageMeta = ({ title, description, image, path, jsonLd } = {}) => {
  const ld = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    if (!title) return;
    const url = `https://qtvstream.com${path || window.location.pathname}`;
    const img = image || DEFAULT_IMAGE;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);
    setCanonical(url);
    let script = null;
    if (ld) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.pageLd = "true";
      script.textContent = ld;
      document.head.appendChild(script);
    }
    return () => script?.remove();
  }, [title, description, image, path, ld]);
};

export const channelMeta = (channel, path) => {
  if (!channel) return {};
  const topics = channel.topic_options?.join(", ");
  const url = `https://qtvstream.com${path}`;
  const subtitle = topics && topics.length <= 45 ? topics : channel.tagline;
  return {
    title: `${channel.name} | ${subtitle} — Free on ${SITE}`,
    description: `${channel.description} Watch ${channel.name} free on ${SITE} — no subscription.`,
    image: channel.image,
    path,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TelevisionChannel",
      name: channel.name,
      description: channel.description,
      url,
      image: channel.image,
      broadcastServiceTier: "Free",
      genre: channel.topic_options || undefined,
      provider: { "@type": "Organization", name: SITE, url: "https://qtvstream.com/" },
    },
  };
};

export const videoMeta = (video, channel, path) => {
  if (!video) return {};
  const thumb = video.youtube_id ? `https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg` : undefined;
  const chName = channel?.name || SITE;
  const description = `${video.description || video.title} — watch free on ${chName}.`.slice(0, 300);
  return {
    title: `${video.title} | ${chName}`,
    description,
    image: thumb,
    path,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: video.title,
      description,
      thumbnailUrl: thumb ? [thumb] : undefined,
      uploadDate: video.published_at || undefined,
      embedUrl: video.youtube_id ? `https://www.youtube.com/embed/${video.youtube_id}` : undefined,
      publisher: { "@type": "Organization", name: SITE, url: "https://qtvstream.com/" },
    },
  };
};
